<template>
  <div class="musubi-page musubi-page--crm">
    <header class="musubi-page__head">
      <h1>CRM connections</h1>
      <p class="text-muted">
        Connect your workspace's own CRM so promoted prospects flow straight into it.
        When a lead is promoted, Yorlin pushes the company, contact and an opportunity
        (with the engagement context) into every connected CRM. Tokens are encrypted at
        rest and never shown back in full.
      </p>
    </header>

    <div v-if="loading" class="text-muted">Loading…</div>
    <div v-else-if="error" class="alert alert-danger py-2 small">{{ error }}</div>

    <template v-else>
      <MusubiSectionCard
        v-for="c in connectors"
        :key="c.key"
        :title="c.label"
        icon="shuffle"
        class="crm-card-gap"
      >
        <template #actions>
          <span v-if="c.configured" class="crm-status crm-status--ok"><CygnusIcon name="check-circle" class="me-1" />Connected</span>
          <span v-else class="crm-status crm-status--off">Not connected</span>
        </template>

        <div v-for="f in c.fields" :key="f.key" class="crm-field">
          <div class="crm-field__head">
            <span class="crm-field__label">{{ f.label }}</span>
            <span v-if="f.set" class="crm-field__hint">set</span>
          </div>
          <input
            v-model="form[f.key]"
            :type="f.secret ? 'password' : 'text'"
            class="form-control form-control-sm"
            :autocomplete="f.secret ? 'new-password' : 'off'"
            :placeholder="f.secret ? (f.masked || f.placeholder) : f.placeholder"
          >
          <p v-if="f.help" class="crm-field__help">{{ f.help }}</p>
          <p v-if="f.secret" class="crm-field__help">
            Leave blank to keep the current token. Type <code>(clear)</code> + save to remove it.
          </p>
        </div>

        <div class="crm-actions">
          <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="testing[c.key]" @click="test(c)">
            <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="testing[c.key]" />Test connection
          </button>
          <div v-if="testResult[c.key]" :class="['crm-actions__msg', testResult[c.key].ok ? 'is-ok' : 'is-error']">
            {{ testResult[c.key].message }}
          </div>
        </div>
      </MusubiSectionCard>

      <div class="crm-save">
        <button type="button" class="btn btn-primary btn-sm" :disabled="saving" @click="save">
          <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="saving" />
          <span v-else>Save</span>
        </button>
        <div v-if="saveMessage" :class="['crm-save__msg', saveOk ? 'is-ok' : 'is-error']">{{ saveMessage }}</div>
      </div>

      <MusubiSectionCard title="Recent push activity" icon="refresh" class="crm-card-gap" subtitle="Newest first">
        <table v-if="recentLog.length" class="crm-log">
          <thead><tr><th>When</th><th>Prospect</th><th>CRM</th><th>Status</th><th>Detail</th></tr></thead>
          <tbody>
            <tr v-for="l in recentLog" :key="l.id">
              <td class="text-muted small">{{ fmtDate(l.created) }}</td>
              <td><a :href="`/acquire/prospects/${l.prospect_id}`">#{{ l.prospect_id }}</a></td>
              <td>{{ l.connector }}</td>
              <td>
                <span v-if="l.status === 'pushed'" class="text-success">pushed</span>
                <span v-else-if="l.status === 'skipped'" class="text-muted">skipped</span>
                <span v-else class="text-danger">failed</span>
              </td>
              <td class="small text-muted">{{ l.error || '—' }}</td>
            </tr>
          </tbody>
        </table>
        <p v-else class="text-muted small">No pushes yet. Promote a prospect to send it to a connected CRM.</p>
      </MusubiSectionCard>
    </template>
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";

const ENDPOINT = '/api/v1/admin/integrations/crm-connections';

export default {
  name: 'AdminCrmConnections',
  props: { componentData: { type: Object, default: () => ({}) } },
  data() {
    return {
      api: useApi(),
      connectors: [],
      recentLog: [],
      form: {},
      testing: {},
      testResult: {},
      loading: true,
      saving: false,
      error: null,
      saveMessage: '',
      saveOk: false,
    };
  },
  mounted() { this.load(); },
  methods: {
    async load() {
      this.loading = true;
      this.error = null;
      try {
        const res = await this.api.get(ENDPOINT);
        const p = res?.data ?? res;
        this.connectors = p.connectors || [];
        this.recentLog = p.recent_log || [];
        // Secret fields stay blank (masked); plain fields prime with their value.
        const form = {};
        this.connectors.forEach((c) => {
          (c.fields || []).forEach((f) => { form[f.key] = f.secret ? '' : (f.value || ''); });
        });
        this.form = form;
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Failed to load CRM connections.';
      } finally {
        this.loading = false;
      }
    },
    async save() {
      this.saving = true;
      this.saveMessage = '';
      this.saveOk = false;
      const body = {};
      this.connectors.forEach((c) => {
        (c.fields || []).forEach((f) => {
          const v = this.form[f.key];
          if (f.secret) {
            // Skip blank secrets (keep current); '(clear)' removes.
            if (v === '(clear)') body[f.key] = '(clear)';
            else if (v) body[f.key] = v;
          } else {
            // Always send plain fields (covers update + explicit clear).
            body[f.key] = v ?? '';
          }
        });
      });
      try {
        const res = await this.api.put(ENDPOINT, body);
        const payload = res?.data ?? res;
        if (payload?.success) {
          this.saveOk = true;
          this.saveMessage = 'Saved.';
          await this.load();
        } else {
          this.saveMessage = payload?.error || 'Save failed.';
        }
      } catch (e) {
        this.saveMessage = e?.response?.data?.error || e?.message || 'Save failed.';
      } finally {
        this.saving = false;
      }
    },
    async test(c) {
      this.testing = { ...this.testing, [c.key]: true };
      this.testResult = { ...this.testResult, [c.key]: null };
      try {
        const res = await this.api.post(`${ENDPOINT}/${c.key}/test`, {});
        const p = res?.data ?? res;
        this.testResult = { ...this.testResult, [c.key]: { ok: !!p.ok, message: p.message || (p.ok ? 'Connected.' : 'Failed.') } };
      } catch (e) {
        this.testResult = { ...this.testResult, [c.key]: { ok: false, message: e?.response?.data?.message || e?.message || 'Test failed.' } };
      } finally {
        this.testing = { ...this.testing, [c.key]: false };
      }
    },
    fmtDate(ts) { return ts ? new Date(ts * 1000).toLocaleString() : '—'; },
  },
};
</script>

<style scoped>
.musubi-page--crm { padding: var(--m-space-6, 32px); max-width: 720px; }
.crm-card-gap { margin-top: 18px; }

.crm-status { font-size: 0.76rem; font-weight: 600; }
.crm-status--ok { color: var(--m-status-success, #10B981); }
.crm-status--off { color: var(--m-text-tertiary); }

.crm-field { display: flex; flex-direction: column; gap: 5px; margin-bottom: 14px; }
.crm-field:last-child { margin-bottom: 0; }
.crm-field__head { display: flex; justify-content: space-between; align-items: baseline; gap: 8px; }
.crm-field__label { font-size: 0.85rem; font-weight: 500; color: var(--m-text-primary); }
.crm-field__hint { font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--m-text-tertiary); }
.crm-field__help { font-size: 0.76rem; color: var(--m-text-secondary); margin: 2px 0 0; }

.crm-actions { display: flex; align-items: center; gap: 10px; margin-top: 12px; }
.crm-actions__msg { font-size: 0.82rem; }
.crm-actions__msg.is-ok { color: var(--m-status-success, #10B981); }
.crm-actions__msg.is-error { color: var(--m-status-danger, #EF4444); }

.crm-save { display: flex; align-items: center; gap: 10px; margin-top: 18px; }
.crm-save__msg { font-size: 0.85rem; }
.crm-save__msg.is-ok { color: var(--m-status-success, #10B981); }
.crm-save__msg.is-error { color: var(--m-status-danger, #EF4444); }

.crm-log { width: 100%; border-collapse: collapse; }
.crm-log th { text-align: left; font-size: 0.66rem; text-transform: uppercase; letter-spacing: 0.04em; color: var(--m-text-tertiary); font-weight: 600; padding: 7px 6px; border-bottom: 1px solid var(--m-border); }
.crm-log td { padding: 8px 6px; border-bottom: 1px solid var(--m-border); vertical-align: middle; font-size: 0.85rem; }
</style>
